import * as React from 'react'
import { NumberFormatBase, type NumberFormatValues } from 'react-number-format'

import { Input } from '@/components/ui/input'
import { formatIban, getIbanMaxLength, normalizeIban } from '@/lib/iban'
import { cn } from '@/lib/utils'

type IbanInputProps = Omit<
  React.ComponentProps<'input'>,
  'children' | 'defaultValue' | 'onChange' | 'type' | 'value'
> & {
  value?: string | null
  onChange: (value: string) => void
}

const IBAN_PLACEHOLDER = 'AT00 0000 0000 0000 0000'

function getFormattedMaxLength(electronicLength: number): number {
  return electronicLength + Math.floor((electronicLength - 1) / 4)
}

const IbanInput = React.forwardRef<HTMLInputElement, IbanInputProps>(
  ({ className, onChange, placeholder = IBAN_PLACEHOLDER, value, ...props }, ref) => {
    const normalizedValue = normalizeIban(value)
    const maxElectronicLength = getIbanMaxLength(normalizedValue)

    return (
      <NumberFormatBase
        customInput={Input}
        getInputRef={ref}
        type="text"
        autoCapitalize="characters"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        inputMode="text"
        placeholder={placeholder}
        maxLength={getFormattedMaxLength(maxElectronicLength)}
        value={normalizedValue}
        format={formatIban}
        removeFormatting={normalizeIban}
        isValidInputCharacter={(character) => /[a-zA-Z0-9]/.test(character)}
        isAllowed={(values) => {
          const nextValue = normalizeIban(values.formattedValue)
          return nextValue.length <= getIbanMaxLength(nextValue)
        }}
        onValueChange={(values: NumberFormatValues) => {
          onChange(normalizeIban(values.value).slice(0, getIbanMaxLength(values.value)))
        }}
        className={cn('font-mono uppercase tracking-[0.16em]', className)}
        {...props}
      />
    )
  },
)

IbanInput.displayName = 'IbanInput'

export { IbanInput }
